import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { createCheckoutSession } from '../api/checkout';

function formatPrice(cents) {
  return '$' + (cents / 100).toFixed(2);
}

export default function Checkout() {
  const { cart, updateItem, removeItem } = useCart();
  const { user } = useAuth();
  const [shippingForm, setShippingForm] = useState({ name: '', email: '', line1: '', line2: '', city: '', state: '', zip: '' });
  const [saveAddress, setSaveAddress] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pre-fill from user's saved address
  useEffect(() => {
    if (user) {
      const addr = user.shipping_address || {};
      setShippingForm(f => ({
        ...f,
        name: addr.name || user.name || f.name,
        email: user.email || f.email,
        line1: addr.line1 || f.line1,
        line2: addr.line2 || f.line2,
        city: addr.city || f.city,
        state: addr.state || f.state,
        zip: addr.zip || f.zip,
      }));
    }
  }, [user]);

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!cart.items.length) return;
    setError('');
    setLoading(true);
    try {
      const data = {
        shipping: {
          name: shippingForm.name,
          line1: shippingForm.line1,
          line2: shippingForm.line2,
          city: shippingForm.city,
          state: shippingForm.state,
          zip: shippingForm.zip,
          country: 'US',
          ...(user ? {} : { email: shippingForm.email }),
        },
        save_address: saveAddress,
      };

      const res = await createCheckoutSession(data);
      // Redirect to Stripe hosted checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : detail?.error || 'Checkout failed. Please try again.');
      setLoading(false);
    }
  };

  if (!cart.items.length) {
    return (
      <div className="container section" style={{ textAlign: 'center' }}>
        <h2>Your Cart is Empty</h2>
        <p style={{ color: 'var(--warm-gray)', margin: '16px 0' }}>Add some items to get started.</p>
        <Link to="/shop" className="btn btn-primary">Shop Now</Link>
      </div>
    );
  }

  return (
    <div className="container section">
      <h1 style={{ marginBottom: '32px' }}>Checkout</h1>

      <div className="checkout-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '48px', alignItems: 'start' }}>
        {/* Left: Form */}
        <form onSubmit={handleCheckout}>
          {!user && (
            <>
              <h3 style={{ marginBottom: '16px' }}>Contact</h3>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={shippingForm.email} onChange={e => setShippingForm(f => ({ ...f, email: e.target.value }))} required placeholder="your@email.com" />
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--warm-gray)', marginBottom: '24px' }}>
                <Link to="/login" style={{ color: 'var(--forest)' }}>Log in</Link> for faster checkout
              </p>
            </>
          )}

          <h3 style={{ marginBottom: '16px' }}>Shipping Address</h3>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input className="form-input" value={shippingForm.name} onChange={e => setShippingForm(f => ({ ...f, name: e.target.value }))} required />
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <input className="form-input" value={shippingForm.line1} onChange={e => setShippingForm(f => ({ ...f, line1: e.target.value }))} required />
          </div>
          <div className="form-group">
            <label className="form-label">Apt / Suite (optional)</label>
            <input className="form-input" value={shippingForm.line2} onChange={e => setShippingForm(f => ({ ...f, line2: e.target.value }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-input" value={shippingForm.city} onChange={e => setShippingForm(f => ({ ...f, city: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input className="form-input" value={shippingForm.state} onChange={e => setShippingForm(f => ({ ...f, state: e.target.value }))} required maxLength={2} placeholder="AZ" />
            </div>
            <div className="form-group">
              <label className="form-label">ZIP</label>
              <input className="form-input" value={shippingForm.zip} onChange={e => setShippingForm(f => ({ ...f, zip: e.target.value }))} required />
            </div>
          </div>

          {user && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', margin: '8px 0 16px' }}>
              <input type="checkbox" checked={saveAddress} onChange={e => setSaveAddress(e.target.checked)} />
              Save this address to my account
            </label>
          )}

          {error && <p className="form-error" style={{ marginBottom: '16px' }}>{error}</p>}

          <button className="btn btn-gold btn-lg" type="submit" disabled={loading} style={{ width: '100%', marginTop: '8px' }}>
            {loading ? 'Redirecting to payment...' : 'Continue to Payment'}
          </button>
        </form>

        {/* Right: Cart Summary */}
        <div className="cart-summary" style={{ position: 'sticky', top: '100px' }}>
          <h3 style={{ marginBottom: '16px' }}>Order Summary</h3>
          {cart.items.map(item => (
            <div className="cart-item" key={item.item_id}>
              <div className="cart-item-img">
                {item.image_url ? <img src={item.image_url} alt="" /> : null}
              </div>
              <div className="cart-item-details">
                <div className="cart-item-name">{item.product_name}</div>
                {(item.variant || item.color) && <div className="cart-item-variant">{item.variant || item.color}</div>}
                <div className="cart-item-actions">
                  <button className="qty-btn" onClick={() => updateItem(item.item_id, { quantity: item.quantity - 1 })}>-</button>
                  <span>{item.quantity}</span>
                  <button className="qty-btn" onClick={() => updateItem(item.item_id, { quantity: item.quantity + 1 })}>+</button>
                  <button onClick={() => removeItem(item.item_id)} style={{ background: 'none', border: 'none', color: 'var(--error)', fontSize: '0.75rem', marginLeft: 'auto' }}>Remove</button>
                </div>
              </div>
              <div className="cart-item-price">{formatPrice(item.price_cents * item.quantity)}</div>
            </div>
          ))}

          <div className="cart-totals">
            <div className="cart-total-row"><span>Subtotal</span><span>{formatPrice(cart.subtotal_cents)}</span></div>
            <div className="cart-total-row">
              <span>Shipping</span>
              <span>{cart.shipping_cents === 0 ? 'FREE' : formatPrice(cart.shipping_cents)}</span>
            </div>
            {cart.shipping_cents > 0 && (
              <p style={{ fontSize: '0.75rem', color: 'var(--warm-gray)', marginTop: '4px' }}>
                Free shipping on orders over {formatPrice(cart.free_shipping_threshold_cents)}
              </p>
            )}
            <div className="cart-total-row grand"><span>Total</span><span>{formatPrice(cart.total_cents)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
